/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn, AliasType } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './AvatarList';

const AvatarListManager: ComponentManagerFn<'AvatarList'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AvatarList'>(platform, 'AvatarList'),
    type: 'AvatarList',
    name: 'Avatar List',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'Popover'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
        ],
        required: true,
      },
      viewAttribute: {
        label: 'View Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        required: true,
        getAttributeDataTypes: () => ['String'],
        helpText: 'Used to derive the Intials to be displayed',
      },
      maxAvatars: {
        label: 'Max Avatars',
        type: 'number',
        min: 4,
        max: 20,
      },
      size: {
        label: 'Size',
        type: 'number',
        min: 24,
        max: 40,
        step: 4,
      },
      displayAsStack: {
        label: 'Display As Stack',
        type: 'boolean',
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.alias,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'alias' } : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    category: 'Forms',
    icon: { iconName: 'users' },
    description: `**Overview**<br/>
The Avatar List component displays a horizontal sequence of user avatars (profile circles) representing a group of people.
It is commonly used to show Team Members, Meeting Attendees, or Collaborators on a project card. If a list is too long, it automatically truncates the display and shows a numeric overflow indicator (e.g., "+5").

**1. Data Binding**<br/>
This component requires a connection to a Data Store to retrieve the list of users.
* **Data Store Alias:** Select the Data Store (e.g., "ProjectMembers" or "Assignees") containing the records.
* **View Attribute:** Select the String attribute representing the user's name (e.g., "FullName").
* **Usage:** This string is used to generate the Initials displayed inside the avatar circle (e.g., "John Doe" → "JD").

**2. Visual Configuration**<br/>
You can customize the density and layout of the list to fit your UI.
* **Display As Stack:**
    * **Enabled:** The avatars overlap slightly (like a deck of cards), creating a compact, modern look ideal for saving horizontal space.
    * **Disabled:** The avatars are placed side-by-side with standard spacing.
* **Size:** Controls the diameter of each circle in pixels. (Range: 24px (Small) to 40px (Large)).
* **Max Avatars:** Sets the limit on how many individual circles are shown. (Behavior: If the data store has 10 records but you set this to 4, the component renders 4 circles and a generic circle labeled "+6").

**3. Interaction**<br/>
* **TriggerOnClick:** You can add this child component to execute logic when a user clicks on an avatar. The trigger automatically receives the context of the specific record clicked.
* **Popover:** You can attach a Popover to show more details (like a full profile card) when hovering over or clicking an avatar.`,
  };
};

export default AvatarListManager;
